import { matchPath } from "react-router-dom";
import { ENTITY_TYPES, ENTITY_URL_PARAMS } from "../constants/entityTypes";

/**
 * route templates for analytics
 * - keep in sync with src/routers/routes (an unlisted route shows up in
 *   Mixpanel as `not_found`)
 */
const ROUTE_TEMPLATES = [
  // RoutesMain
  { path: "/" },
  { path: "/landing" },
  { path: "/browse" },
  { path: "/browse/track/:id" },
  { path: "/order" },
  { path: "/order/:orderId" },
  { path: "/subscription" },
  { path: "/subscription/:subscriptionId" },
  { path: "/playlists" },
  { path: "/playlist/:id" },
  { path: "/album/:id" },
  { path: "/contact" },
  { path: "/licensing" },
  { path: "/licensing/enterprise" },
  { path: "/licensing/pricing-tiers" },
  { path: "/licensing/creative-commons" },
  { path: "/order-confirmation/:id" },
  { path: "/payment-return" },
  // RoutesUser
  { path: "/favorites" },
  { path: "/settings", exact: false },
  { path: "/orders" },
  { path: "/project" },
  { path: "/project/:id" },
  { path: "/project/:id/downloads" },
  { path: "/logout" },
  // RoutesGuest / RoutesUser redirect sources
  { path: "/home" },
  { path: "/login" },
  // RoutesAuth (modal routes)
  { path: "/signup" },
  { path: "/signup/:email" },
  { path: "/signin" },
  { path: "/reset" },
  { path: "/set-password/:email/:password" },
  // RoutesAdmin (root="/admin")
  { path: "/admin" },
  { path: "/admin/upload" },
  { path: "/admin/reindex" }
];

export const routeTemplate = pathname => {
  const match = ROUTE_TEMPLATES.find(({ path, exact = true }) =>
    matchPath(pathname, { path, exact })
  );
  return match ? match.path : "not_found";
};

// routes whose pathname carries an email (or password)
const SENSITIVE_ROUTES = ["/signup/:email", "/set-password/:email/:password"];

const NON_PAGE_ROUTES = ["/logout"];

// the only query params safe to send as part of `path`
const PATH_QUERY_ALLOWLIST = [...ENTITY_URL_PARAMS, "mode"];

const entityInQuery = (route, params) =>
  route === "/browse"
    ? ENTITY_TYPES.find(
        e => params.has(e.urlParamId) || params.has(e.urlParamName)
      )
    : undefined;

const sanitizedPath = (route, { pathname, search }) => {
  if (route === "not_found" || SENSITIVE_ROUTES.includes(route)) return route;

  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();

  params.forEach((value, key) => {
    if (PATH_QUERY_ALLOWLIST.includes(key)) kept.append(key, value);
  });
  const query = kept.toString();

  return query ? `${pathname}?${query}` : pathname;
};

export const pageViewProperties = location => {
  const route = routeTemplate(location.pathname);
  const entity = entityInQuery(route, new URLSearchParams(location.search));

  return {
    route,
    path: sanitizedPath(route, location),
    ...(entity && { entity_type: entity.typename.toLowerCase() })
  };
};

// identity of a "real" page: query-only rewrites (filters, sort, segment) keep
// the same key. null = not a page: a non-page route, or an entity name still
// resolving to an id
export const pageViewKey = location => {
  const route = routeTemplate(location.pathname);
  if (NON_PAGE_ROUTES.includes(route)) return null;

  const params = new URLSearchParams(location.search);
  const entity = entityInQuery(route, params);
  const entityId = entity ? params.get(entity.urlParamId) : null;

  if (entity && !entityId) return null;
  return `${location.pathname}|${entity ? entity.typename : ""}|${entityId ||
    ""}`;
};
